import { EmailDetails } from '../types/types';

export class EmailService {
  private static itemId: string | null = null;

  static async getEmailDetails(): Promise<EmailDetails> {
    return new Promise((resolve, reject) => {
      try {
        const item = Office.context.mailbox.item;
        this.itemId = item.itemId;
        const details: EmailDetails = {
          subject: item.subject,
          from: item.from?.emailAddress,
          fromName: item.from?.displayName,
          to: item.to?.map((recipient) => ({
            email: recipient.emailAddress,
            name: recipient.displayName,
          })),
          cc: item.cc?.map((recipient) => ({
            email: recipient.emailAddress,
            name: recipient.displayName,
          })),
          bcc: item.bcc && Array.isArray(item.bcc)
            ? item.bcc.map((recipient) => ({
                email: recipient.emailAddress,
                name: recipient.displayName,
              }))
            : [],
          dateTimeCreated: item.dateTimeCreated,
          dateTimeModified: item.dateTimeModified,
          hasAttachments: item.attachments.length > 0,
          attachments: item.attachments.map((attachment) => ({
            name: attachment.name,
            size: attachment.size,
            type: attachment.attachmentType,
          })),
          body: "",
          itemId: item.itemId,
          internetMessageId: item.internetMessageId,
        };

        item.body.getAsync(Office.CoercionType.Html, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            details.body = this.sanitizeHTML(result.value);
            resolve(details);
          } else {
            reject(new Error("Failed to get email body"));
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  static async createEMLFile(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const item = Office.context.mailbox.item;
        
        item.body.getAsync(Office.CoercionType.Html, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const rawHtmlBody = result.value;
            
            const headers = [
              `From: ${item.from?.displayName} <${item.from?.emailAddress}>`,
              `To: ${item.to?.map(recipient => `${recipient.displayName} <${recipient.emailAddress}>`).join(', ')}`,
              `Subject: ${item.subject}`,
              `Date: ${new Date(item.dateTimeCreated).toUTCString()}`,
              item.cc?.length > 0 ? `Cc: ${item.cc.map(recipient => `${recipient.displayName} <${recipient.emailAddress}>`).join(', ')}` : '',
              Array.isArray(item.bcc) && item.bcc.length > 0 ? `Bcc: ${item.bcc.map(recipient => `${recipient.displayName} <${recipient.emailAddress}>`).join(', ')}` : '',
              'MIME-Version: 1.0',
              'Content-Type: text/html; charset=UTF-8',
              'Content-Transfer-Encoding: 8bit',
              '\r\n',
              '\r\n',  
              rawHtmlBody
            ].filter(Boolean).join('\r\n');
            
            const emlBlob = new Blob([headers], { type: 'message/rfc822' });
            resolve(emlBlob);
          } else {
            reject(new Error("Failed to get email body"));
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  private static sanitizeHTML(html: string): string {
    // Remove warning banner
    html = html.replace(/<table[^>]*>[\s\S]*?WARNING[\s\S]*?<\/table>/gi, "");

    // Remove image tags
    html = html.replace(/<img[^>]*>/gi, "");

    // Remove style attributes
    html = html.replace(/\s+style="[^"]*"/gi, "");

    // Remove class attributes
    html = html.replace(/\s+class="[^"]*"/gi, "");

    // Remove data attributes
    html = html.replace(/\s+data-[^=]*="[^"]*"/gi, "");

    // Remove id attributes
    html = html.replace(/\s+id="[^"]*"/gi, "");

    // Remove dir attributes
    html = html.replace(/\s+dir="[^"]*"/gi, "");

    // Convert divs to paragraphs
    html = html.replace(/<\/?div[^>]*>/gi, (match) => match.toLowerCase().replace("div", "p"));

    // Fix nested paragraphs
    html = html.replace(/<p>\s*(<p[^>]*>)/gi, "$1");
    html = html.replace(/<\/p>\s*<\/p>/gi, "</p>");

    // Remove empty paragraphs
    html = html.replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "");

    // Remove blockquotes
    html = html.replace(/<\/?blockquote[^>]*>/gi, "");

    // Replace multiple br tags
    html = html.replace(/(<br\s*\/?>\s*){2,}/gi, "</p><p>");

    // Clean up br tags
    html = html.replace(/\s*<br\s*\/?>\s*<\/p>/gi, "</p>");
    html = html.replace(/<p[^>]*>\s*<br\s*\/?>\s*/gi, "<p>");

    // Clean up font tags
    html = html.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");

    // Clean up empty spans
    html = html.replace(/<span[^>]*>(\s*)<\/span>/gi, "$1");

    // Remove nested tags
    html = html.replace(/<(\w+)[^>]*><\1[^>]*>/gi, "<$1>");
    html = html.replace(/<\/(\w+)><\/\1>/gi, "</$1>");

    // Fix spacing and newlines
    html = html.replace(/\\n/g, "");
    html = html.replace(/\n+/g, "");
    html = html.replace(/\s+/g, " ");
    html = html.replace(/>\s+</g, "><");
    html = html.replace(/([.,!?])\s*</g, "$1 <");
    html = html.replace(/<\/p><p>/g, "</p>\n<p>");
    html = html.replace(/<p>\s+/g, "<p>");
    html = html.replace(/\s+<\/p>/g, "</p>");
    html = html.replace(/(<\/p>\s*)+(<p>\s*)+/g, "</p>\n<p>");

    return html.trim();
  }
}